import type { StreamingBlock } from "@storytell/streaming";
import { BlockRenderer } from "@storytell/streaming";
import { Match, Show, Switch, createEffect, createSignal } from "solid-js";
import { TypingAnimation } from "~/components/common/TypingAnimation";
import { ConceptsRenderer } from "./ConceptsRenderer";
import { ImageResultsRenderer } from "./ImageResultsRenderer";
import { SearchResultsRenderer } from "./SearchResultsRenderer";
import { StructuredResultsRenderer } from "./StructuredResultsRenderer";
import { WebSearchResultsRenderer } from "./WebSearchResultsRenderer";
import { BlockHeaderPill } from "./internal/BlockHeaderPill";
import { StreamingMarkdownSmall } from "./internal/StreamingMarkdownSmall";
import { copyBlockDebugInfoToClipboard } from "./internal/blockDebugUtils";
import { getBlockIconComponent } from "./internal/blockIcons";
import { createMarkdownStreamerFromDeltas } from "./internal/markdownStreamerFromDeltas";

interface InterstitialRendererProps {
  block: StreamingBlock;
  onBlockClick?: () => void;
}

const shouldAutoCollapse = (block: StreamingBlock) => {
  if (!block.isComplete) {
    return false;
  }
  return true;
};

/**
 * Renders interstitial blocks with expandable content. Starts expanded and auto-collapses
 * operations when complete, but keeps true interstitial messages expanded.
 * Shows streaming markdown content with expand/collapse functionality.
 * Subscribes to streaming deltas and appends them to the markdown renderer without re-rendering.
 */
export const InterstitialRenderer = (props: InterstitialRendererProps) => {
  const [isExpanded, setIsExpanded] = createSignal(!shouldAutoCollapse(props.block)); // Start expanded
  const [userExpanded, setUserExpanded] = createSignal(false); // Track if user manually expanded

  const hasContent = () => !!props.block.deltas.length;

  const title = () => props.block.title || props.block.activity || "Processing";

  const markdownStreamer = createMarkdownStreamerFromDeltas(() => props.block.deltas.map((delta) => delta.text));

  // createEffect(() => {
  //   if (shouldAutoCollapse(props.block) && !userExpanded()) {
  //     setTimeout(() => {
  //       setIsExpanded(false);
  //     }, 1000);
  //   }
  // });

  // TODO: @andi streaming
  // Auto-collapse effect - watches for block completion reactively
  // createEffect(() => {
  //   const currentBlock = block();
  //   if (!currentBlock || userExpanded()) return;

  //   // Only auto-collapse when block is complete
  //   if (currentBlock.isComplete) {
  //     const isOperation =
  //       currentBlock.messageType === ChatMessages.MessageType.OPERATION_STATUS ||
  //       currentBlock.messageType === ChatMessages.MessageType.TASK_CREATE ||
  //       currentBlock.messageType === ChatMessages.MessageType.TASK_UPDATE;

  //     if (isOperation) {
  //       // Find which operation contains this block
  //       const operationId = findOperationIdByBlockId(currentBlock.id);

  //       if (operationId) {
  //         // Check if the entire operation is complete, not just this block
  //         const operation = getOperation(operationId)();
  //         const allBlocksComplete = operation?.blocks.every((block) => block.isComplete) || false;

  //         // Only auto-collapse when all blocks in the operation are complete
  //         if (allBlocksComplete) {
  //           setIsExpanded(false);
  //         }
  //       }
  //     } else {
  //       // For non-operation blocks, auto-collapse when the block itself is complete
  //       setIsExpanded(false);
  //     }
  //   }
  // });

  const handleToggleExpanded = () => {
    const newExpanded = !isExpanded();
    setIsExpanded(newExpanded);
    setUserExpanded(true); // Mark as user-controlled
    // Don't call onBlockClick - we only want to toggle the inline content
  };

  const handleCopyDebugInfo = async (e: MouseEvent) => {
    e.stopPropagation();
    await copyBlockDebugInfoToClipboard(props.block);
  };

  return (
    <div class="group" data-renderer="interstitial-renderer">
      <Switch>
        <Match when={props.block.webSearchResults}>
          {(results) => <WebSearchResultsRenderer webSearchResults={results()} />}
        </Match>

        <Match when={props.block.imageResults}>{(results) => <ImageResultsRenderer imageResults={results()} />}</Match>

        <Match when={props.block.searchResults}>
          {(results) => <SearchResultsRenderer searchResults={results()} />}
        </Match>

        <Match when={props.block.structuredResults}>
          {(results) => <StructuredResultsRenderer structuredResults={results()} />}
        </Match>

        <Match when={props.block.conceptResults}>{(results) => <ConceptsRenderer conceptResults={results()} />}</Match>

        <Match when={true}>
          <BlockHeaderPill
            title={title()}
            icon={getBlockIconComponent(props.block, BlockRenderer.INTERSTITIAL, {
              variant: "blue",
            })}
            isProcessing={!props.block.isComplete}
            onClick={handleToggleExpanded}
            onCopyDebugInfo={handleCopyDebugInfo}
            variant="blue"
            hasExpandableContent={hasContent()}
            isExpanded={isExpanded()}
          />

          <Show when={hasContent() && isExpanded()}>
            <div class="mt-2 ml-5">
              <StreamingMarkdownSmall doc={markdownStreamer.doc} />
              <Show when={!props.block.isComplete}>
                <TypingAnimation />
              </Show>
            </div>
          </Show>
        </Match>
      </Switch>
    </div>
  );
};
